interface YahooChartResponse {
  chart: {
    result: {
      timestamp: number[];
      indicators: {
        quote: {
          close: (number | null)[];
          open?: (number | null)[];
          high?: (number | null)[];
          low?: (number | null)[];
          volume?: (number | null)[];
        }[];
      };
      meta: {
        regularMarketPrice: number | null;
        regularMarketDayHigh: number | null;
        regularMarketDayLow: number | null;
        regularMarketTime: number | null;
        regularMarketVolume: number | null;
        chartPreviousClose: number | null;
        firstTradeDate?: number | null;
        exchangeTimezoneName?: string | null;
      } | null;
    }[];
    error: { code: string; description: string } | null;
  };
}

export interface DailyPriceHistory {
  prices: { date: string; price: number }[];
  inceptionDate: string | null;
}

export interface IntradayPriceHistory {
  prices: { timestamp: number; price: number }[];
}

interface DailyBar {
  date: string;
  close: number;
  open: number | null;
  high: number | null;
  low: number | null;
  volume: number | null;
}

function isIsolatedInvalidDailyBar(bars: DailyBar[], index: number): boolean {
  const previous = bars[index - 1];
  const current = bars[index];
  const next = bars[index + 1];
  if (!previous || !current || !next || current.volume !== 0) return false;
  const flat = [current.open, current.high, current.low].every(
    (value) => value !== null && Math.abs(value - current.close) <= current.close * 0.000001
  );
  if (!flat) return false;
  const neighborsAgree = Math.abs(next.close / previous.close - 1) <= 0.05;
  const differsFromPrevious = Math.abs(current.close / previous.close - 1) >= 0.1;
  const differsFromNext = Math.abs(current.close / next.close - 1) >= 0.1;
  return neighborsAgree && differsFromPrevious && differsFromNext;
}

function timestampToDate(timestamp: number, timeZone?: string | null): string {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date(timestamp * 1000));
      const values = new Map(parts.map((part) => [part.type, part.value]));
      return `${values.get('year')}-${values.get('month')}-${values.get('day')}`;
    } catch {
      // Fall back to UTC when the provider returns an unknown time zone.
    }
  }
  return new Date(timestamp * 1000).toISOString().slice(0, 10);
}

export async function fetchDailyPrices(ticker: string, startDate?: string, endDate?: string): Promise<DailyPriceHistory> {
  const periodQuery =
    startDate && endDate
      ? `period1=${Math.floor(new Date(`${startDate}T00:00:00Z`).getTime() / 1000)}&period2=${Math.floor(
          new Date(`${endDate}T00:00:00Z`).getTime() / 1000
        )}`
      : 'range=max';
  const url =
    `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(ticker)}` + `?interval=1d&${periodQuery}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Yahoo Finance request failed: ${response.status} ${response.statusText}`);
  }

  const data: YahooChartResponse = await response.json();
  if (data.chart.error) {
    throw new Error(`Yahoo Finance error: ${data.chart.error.description}`);
  }

  const result = data.chart.result?.[0];
  if (!result) throw new Error('No data returned from Yahoo Finance');
  const closes = result.indicators.quote[0]?.close;
  if (!closes) throw new Error('No price data in Yahoo Finance response');

  const timeZone = result.meta?.exchangeTimezoneName;
  const quote = result.indicators.quote[0]!;
  const bars = result.timestamp.flatMap((timestamp, index): DailyBar[] => {
    const close = closes[index];
    return close == null
      ? []
      : [
          {
            date: timestampToDate(timestamp, timeZone),
            close,
            open: quote.open?.[index] ?? null,
            high: quote.high?.[index] ?? null,
            low: quote.low?.[index] ?? null,
            volume: quote.volume?.[index] ?? null,
          },
        ];
  });
  const prices = bars
    .filter((_, index) => !isIsolatedInvalidDailyBar(bars, index))
    .map((bar) => ({ date: bar.date, price: Math.round(bar.close * 10000) }));
  const firstTradeDate = result.meta?.firstTradeDate;

  return {
    prices,
    inceptionDate: firstTradeDate == null ? null : timestampToDate(firstTradeDate, timeZone),
  };
}

export async function fetchIntradayPrices(ticker: string): Promise<IntradayPriceHistory> {
  const url =
    `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(ticker)}` +
    '?interval=5m&range=5d&includePrePost=false';
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Yahoo Finance request failed: ${response.status} ${response.statusText}`);
  }
  const data: YahooChartResponse = await response.json();
  if (data.chart.error) throw new Error(`Yahoo Finance error: ${data.chart.error.description}`);
  const result = data.chart.result?.[0];
  if (!result) throw new Error('No intraday data returned from Yahoo Finance');
  const closes = result.indicators.quote[0]?.close;
  if (!closes) throw new Error('No intraday price data in Yahoo Finance response');
  return {
    prices: result.timestamp.flatMap((timestamp, index) => {
      const close = closes[index];
      return close == null ? [] : [{ timestamp, price: Math.round(close * 10000) }];
    }),
  };
}

export async function fetchMonthlyPrices(
  ticker: string,
  startDate: string,
  endDate: string
): Promise<{ yearMonth: string; price: number }[]> {
  const period1 = Math.floor(new Date(startDate).getTime() / 1000);
  const period2 = Math.floor(new Date(endDate).getTime() / 1000);

  const url =
    `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(ticker)}` +
    `?interval=1mo&period1=${period1}&period2=${period2}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Yahoo Finance request failed: ${response.status} ${response.statusText}`);
  }

  const data: YahooChartResponse = await response.json();

  if (data.chart.error) {
    throw new Error(`Yahoo Finance error: ${data.chart.error.description}`);
  }

  const result = data.chart.result?.[0];
  if (!result) {
    throw new Error('No data returned from Yahoo Finance');
  }

  const timestamps = result.timestamp;
  const closes = result.indicators.quote[0]?.close;
  if (!closes) {
    throw new Error('No price data in Yahoo Finance response');
  }

  const prices: { yearMonth: string; price: number }[] = [];

  for (let i = 0; i < timestamps.length; i++) {
    const close = closes[i];
    if (close == null) continue;

    const date = new Date(timestamps[i]! * 1000);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const yearMonth = `${year}-${month}`;

    prices.push({
      yearMonth,
      price: Math.round(close * 10000),
    });
  }
  return prices;
}

export async function fetchCurrentPrice(ticker: string): Promise<{ price: number }> {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const period1 = Math.floor(new Date(weekAgo).getTime() / 1000);
  const period2 = Math.floor(new Date(today).getTime() / 1000);

  const url =
    `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(ticker)}` +
    `?interval=1d&period1=${period1}&period2=${period2}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Yahoo Finance request failed: ${response.status} ${response.statusText}`);
  }

  const data: YahooChartResponse = await response.json();

  if (data.chart.error) {
    throw new Error(`Yahoo Finance error: ${data.chart.error.description}`);
  }
  const meta = data.chart.result?.[0]?.meta;
  const price = meta?.regularMarketPrice ?? meta?.chartPreviousClose;
  if (price != null) {
    return { price: Math.round(price * 10000) };
  }

  const result = data.chart.result?.[0];
  if (!result) {
    throw new Error('No data returned from Yahoo Finance');
  }

  const closes = result.indicators.quote[0]?.close;
  if (!closes) {
    throw new Error('No price data in Yahoo Finance response');
  }

  for (let i = closes.length - 1; i >= 0; i--) {
    const close = closes[i];
    if (close != null) {
      return { price: Math.round(close * 10000) };
    }
  }

  throw new Error('No valid price found in Yahoo Finance response');
}
